import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import CvTables from "./CvTables";
import { buildCondition } from "../../../static/hook";
import useFetchBaseQuery from "../../../api/baseQueries";
import { useSearchParams } from "react-router-dom";
import CustomSpinner from "../../../components/CustomSpinner";
import MenuIcon from "@mui/icons-material/Menu";
import { cvStatusOptions, packageCodes, paymentStatusOptions, stStatusOptions } from "../../../static/dropdown";
import NotAuthorize from "../../../error/NotAuthorize";

const CvDashboard = ({ user, setShowSideOverlay }) => {
  const [searchParams] = useSearchParams();
  const userCode = parseInt(searchParams.get("userCode"));
  const [authState, setAuthState] = useState(false);
  const [totalReportLoading, setTotalReportLoading] = useState(false);
  const [totalSopReport, setTotalSopReport] = useState(null);
  const [visaSopReportLoading, setVisaSopReportLoading] = useState(false);
  const [visaSopReport, setVisaSopReport] = useState(null);
  const [admSopUserReportLoading, setAdmUserSopReportLoading] = useState(false);
  const [admSopUserReport, setAdmSopUserReport] = useState(null);
  const [cvReportLoading, setCvReportLoading] = useState(false);
  const [cvReport, setCvReport] = useState(null);
  const [userWiseLoading, setUserWiseLoading] = useState(false);
  const [cvUserWiseReport, setUserWiseCvReport] = useState(null);
  const [userWiseVisaLoading, setUserWiseVisaLoading] = useState(false);
  const [visaUserWiseReport, setVisaWiseCvReport] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [spinnerLoading, setSpinnerLoading] = useState(false);
  const [filterDropdown, setFilterDropdown] = useState({
    intakeOptions: [], // Example format: [{ name: 'Intake1', value: 1 }]
    locationOptions: [],
    categoryOptions: [],
    plannerOptions: [],
  });
  const fetchBaseQuery = useFetchBaseQuery();

  const [filterOptions, setFilterOptions] = useState({
    location: [],
    package: [],
    intake: [],
    location : [],
    ststatus: [],
    paymentStatus: [],
    category: [],
    cvStatus : [],
    plannerStatus : [],
  });

  const fetchFilterDropdown = useCallback(async () => {
    try {
      const queries = {
        intakeOptions:
          "SELECT IntakeCode AS 'Code', Intakename AS 'Name' FROM intakemas",
        locationOptions:
          "SELECT LocCode AS 'Code', locname AS 'Name' FROM locmas",
        categoryOptions:
          "SELECT CatCode AS 'Code', CatName AS 'Name' FROM catmas",
        plannerOptions:
          "SELECT compid AS 'Code', Compname AS 'Name' FROM commontypemas WHERE compcode = 'Planner_status'",
      };

      const fetchPromises = Object.entries(queries).map(
        async ([key, query]) => {
          const data = await fetchBaseQuery(query);
          return { key, data };
        }
      );

      const results = await Promise.all(fetchPromises);

      const updatedDropdowns = results.reduce((acc, { key, data }) => {
        if (data?.length) {
          acc[key] = data.map((item) => ({
            label: item.NAME || "Unknown",
            value: item.CODE || "N/A",
          }));
        } else {
          console.warn(`No data received for ${key}`);
        }
        return acc;
      }, {});

      if (Object.keys(updatedDropdowns).length) {
        setFilterDropdown((prev) => ({ ...prev, ...updatedDropdowns }));
      }
    } catch (error) {
      console.error("Error fetching filter dropdown options:", error.message);
    }
  }, [fetchBaseQuery]);

  
  const fetchCvReports = async () => {
    setCvReportLoading(true);
    try {
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );

      const stStatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );

      const paymentCodeCondition = buildCondition(
        "s.PaymentStatus",
        filterOptions.paymentStatus,
      );
      const categoryCodeCondition = buildCondition(
        "s.catCode",
        filterOptions.category,
      );

      [
        intakeCondition,
        packageCondition,
        locationCondition,
        stStatusCondition,
        paymentCodeCondition,
        categoryCodeCondition,
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });

      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";
      const baseQuery = "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN AdmissionSheetMas a ON a.StdCode = s.StdCode LEFT JOIN Stdassignment sa ON s.StdCode = sa.stdcode LEFT JOIN Usermas um ON um.UserCode = sa.UserCode JOIN catmas ct ON ct.CatCode = s.catCode JOIN intakemas i ON i.IntakeCode = s.IntakeCode JOIN locmas lo ON lo.LocCode = s.LocCode";
      const queries = {
        totalStudents: `${baseQuery} WHERE s.stdcode IS NOT NULL ${whereClause}`,
        pendingCv: `${baseQuery} WHERE (a.other13 IS NULL OR a.other13 = '' OR a.other13 IN ('--select--', 'Not Yet Assigned', 'Assigned for Making')) ${whereClause}`,
        inProcess: `${baseQuery} WHERE a.other13 = 'Temp Final CV' ${whereClause}`,
        reviewPending: `${baseQuery} WHERE a.other13 = 'Assigned for Review' ${whereClause}`,
        cvUpload: `${baseQuery} WHERE a.other13 = 'Final CV' AND a.CVupload != '' ${whereClause} `,
        finalCv: `${baseQuery} WHERE a.other13 = 'Final CV' ${whereClause}`,
        cvNotUpload: `${baseQuery} WHERE a.other13 = 'Final CV' AND a.CVupload = '' ${whereClause}`,
        notAssignedStatus: `${baseQuery} WHERE (a.other13 IS NULL OR a.other13 = '' OR a.other13 IN ('--select--')) ${whereClause}`,
        cvNotRequired: `${baseQuery} WHERE a.other13 = 'CV Not Required' ${whereClause}`,
      };

      const responseObj = {};
      for (const query in queries) {
        const result = await fetchBaseQuery(queries[query]);
        responseObj[query] = {
          count: result[0]?.Column1,
          query: queries[query],
        };
      }
      setCvReport(responseObj);
    } catch (error) {
      console.log("error occured in fetchSopReports", error);
      setCvReportLoading(false);
    } finally {
      setCvReportLoading(false);
    }
  };

  // const fetchOtherCordinatorCvReports = async () => {
  //   try {
  //     const filters = [];
  //     const intakeCondition = buildCondition(
  //       "s.intakeCode",
  //       filterOptions.intake,
  //       true
  //     );
  //     const packageCondition = buildCondition(
  //       "s.packageCode",
  //       filterOptions.package,
  //       true
  //     );
  //     const locationCondition = buildCondition(
  //       "s.LocCode",
  //       filterOptions.location,
  //       true
  //     );
  //     const stStatusCondition = buildCondition(
  //       "s.ststatus",
  //       filterOptions.ststatus,
  //       true
  //     );
  //     const paymentCodeCondition = buildCondition(
  //       "s.PaymentStatus",
  //       filterOptions.paymentStatus,
  //     );
  //     [
  //       intakeCondition,
  //       packageCondition,
  //       locationCondition,
  //       stStatusCondition,
  //       paymentCodeCondition,
  //     ].forEach((cond) => {
  //       if (cond) filters.push(cond);
  //     });
  //     const rangeCondition =
  //       dateRange?.length === 2 &&
  //       typeof dateRange[0] === "string" &&
  //       typeof dateRange[1] === "string" &&
  //       dateRange[0].trim() &&
  //       dateRange[1].trim()
  //         ? `AND CAST(s.RegistrDate AS DATE) BETWEEN '${dateRange[0]}' AND '${dateRange[1]}'`
  //         : "";

  //     const allFilters = [...filters];
  //     if (rangeCondition) {
  //       allFilters.push(rangeCondition);
  //     }
  //     const whereClause = allFilters.length
  //       ? `AND ${allFilters.join(" AND ")}`
  //       : ``;

  //     const baseQuery =
  //       "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN AdmissionSheetMas a ON a.StdCode = s.StdCode LEFT JOIN Stdassignment sa ON s.StdCode = sa.stdcode LEFT JOIN Usermas um ON um.UserCode = sa.UserCode WHERE (um.UserCode NOT IN (280, 239, 93) OR um.UserCode IS NULL)";

  //     const queries = {
  //       allStudents: `${baseQuery} ${whereClause}`,
  //       pendingCv: `${baseQuery} AND (a.other13 IS NULL OR a.other13 = '' OR a.other13 IN ('--select--', 'Not Yet Assigned', 'Assigned for Making')) ${whereClause}`,
  //       inProcess: `${baseQuery} AND a.other13 = 'Temp Final CV' ${whereClause}`,
  //       reviewPending: `${baseQuery} AND a.other13 = 'Assigned for Review' ${whereClause} `,
  //       finalCv: `${baseQuery} AND a.other13 = 'Final CV' ${whereClause}`,
  //       cvUpload: `${baseQuery} AND a.CVupload != '' ${whereClause}`,
  //       cvNotUpload: `${baseQuery} AND a.CVupload = '' ${whereClause}`,
  //     };
  //     const responseObj = {
  //       others: {
  //         details: {},
  //       },
  //     };
  //     for (const query in queries) {
  //       try {
  //         const result = await fetchBaseQuery(queries[query]);
  //         responseObj.others.details[query] = {
  //           query: queries[query],
  //           count: result[0]?.Column1 || null,
  //         };
  //       } catch (error) {
  //         console.error(`Error fetching data for ${query}:`, error);
  //         responseObj.others.details[query] = null;
  //       }
  //     }
  //     return responseObj;
  //   } catch (error) {
  //     console.log("error occured fetchOtherCordinatorCvReports", error);
  //   }
  // };


  const fetchCvReportsUserWise = async () => {
    setUserWiseLoading(true);
    try {
      const coordinatorList = await fetchBaseQuery(
        "SELECT um.UserCode, um.UserName, um.Active FROM Usermas um WHERE um.UserCode IN (280, 239, 93)"
      );

      if (!Array.isArray(coordinatorList) || coordinatorList.length === 0) {
        console.log("No records to execute");
        return [];
      }

      // Build all filters
      const filters = [];
      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake, true),
        buildCondition("s.packageCode", filterOptions.package, true),
        buildCondition("s.LocCode", filterOptions.location, true),
        buildCondition("s.ststatus", filterOptions.ststatus),
        buildCondition("s.PaymentStatus", filterOptions.paymentStatus),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      // Handle date range
      const rangeCondition =
        dateRange?.length === 2 &&
        typeof dateRange[0] === "string" &&
        typeof dateRange[1] === "string" &&
        dateRange[0].trim() &&
        dateRange[1].trim()
          ? `CAST(s.RegistrDate AS DATE) BETWEEN '${dateRange[0]}' AND '${dateRange[1]}'`
          : "";

      if (rangeCondition) filters.push(rangeCondition);

      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";
      const baseQuery =
        "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN AdmissionSheetMas a ON a.StdCode = s.StdCode LEFT JOIN Stdassignment sa ON s.StdCode = sa.stdcode LEFT JOIN Usermas um ON um.UserCode = sa.UserCode";
      const queries = {
        allStudents: `${baseQuery} WHERE s.stdcode IS NOT NULL ${whereClause}`,
        pendingCv: `${baseQuery} WHERE (a.other13 IS NULL OR a.other13 = '' OR a.other13 IN ('--select--', 'Not Yet Assigned', 'Assigned for Making')) ${whereClause}`,
        inProcess: `${baseQuery} WHERE a.other13 = 'Temp Final CV' ${whereClause}`,
        reviewPending: `${baseQuery} WHERE a.other13 = 'Assigned for Review' ${whereClause}`,
        finalCv: `${baseQuery} WHERE a.other13 = 'Final CV' ${whereClause}`,
        cvUpload: `${baseQuery} WHERE a.CVupload != '' ${whereClause} `,
        cvNotUpload: `${baseQuery} WHERE a.CVupload = '' ${whereClause}`,
        notAssignedStatus: `${baseQuery} WHERE (a.other13 IS NULL OR a.other13 = '' OR a.other13 IN ('--select--', 'Not Yet Assigned', 'Assigned for Making')) ${whereClause}`,
        cvNotRequired: `${baseQuery} WHERE a.other13 = 'CV Not Required' ${whereClause}`,
      };

      const responseObj = [];

      for (const item of coordinatorList) {
        const coordinator = item.USERCODE;
        const coordinatorData = {
          name: item?.USERNAME,
          status: item?.ACTIVE,
          details: {},
        };

        for (const [key, baseQuery] of Object.entries(queries)) {
          // Inject counsellor condition
          const finalQuery = `${baseQuery} AND um.UserCode = '${coordinator}'`;

          try {
            const result = await fetchBaseQuery(finalQuery);
            coordinatorData.details[key] = {
              query: finalQuery,
              count: result[0]?.Column1 || 0,
            };
          } catch (error) {
            console.error(`Error fetching ${key} for ${coordinator}:`, error);
            coordinatorData.details[key] = null;
          }
        }

        responseObj.push(coordinatorData);
      }
      // const otherData = await fetchOtherCordinatorCvReports();
      // const otherDetails = otherData?.others?.details || {};
      // responseObj.push({ name: "other", details: otherDetails });

      setUserWiseCvReport(responseObj);
    } catch (error) {
      console.error("Failed to fetch coordinator reports", error);
      setUserWiseLoading(false);
    } finally {
      setUserWiseLoading(false);
    }
  };

  const fetchSopReports = async () => {
    setTotalReportLoading(true);
    try {
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );

      const stStatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );
      const packageCodeCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      [
        intakeCondition,
        packageCondition,
        locationCondition,
        stStatusCondition,
        packageCodeCondition,
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });
      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : ``;
      const baseQuery = "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode"
      const baseQueryTwo = "SELECT COUNT(DISTINCT a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode WHERE a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND s.packageCode IN (1, 2, 3, 5, 6, 8, 11, 12) AND s.uinliststatus IN (1, 4, 5, 6)"
      const queries = {
        totalStudents: `${baseQuery} WHERE s.packageCode IN (1, 2, 3, 5, 6, 8, 11, 12) AND s.uinliststatus IN (1, 4, 5, 6) ${whereClause}`,
        approvedApplication: `${baseQueryTwo} ${whereClause}`,
        sopNotRequired: `${baseQueryTwo} AND a.other16 = 'SOP not required' ${whereClause}`,
        assgToFreelancer: `${baseQueryTwo} AND a.other16 = 'Assigned to freelancer' ${whereClause}`,
        assgToYg: `${baseQueryTwo} AND a.other16 = 'Assigned to YG team' ${whereClause}`,
        cvPending: `${baseQueryTwo} AND a.other16 = 'CV Pending' ${whereClause}`,
        sopDraftCreated: `${baseQueryTwo} AND a.other16 = 'SOP draft created' ${whereClause}`,
        firstDraftUploaded: `${baseQueryTwo} AND a.statusFromYGTeam = 1 AND a.other16 = 'First draft uploaded' ${whereClause}`,
        inReview: `${baseQueryTwo} AND a.other16 = 'In Review' ${whereClause}`,
        completed: `${baseQueryTwo} AND a.other16 = 'Completed' ${whereClause}`,
        completed_notUploaded: `${baseQueryTwo} AND a.other16 = 'Completed' AND a.other28 IS NULL ${whereClause}`,
        sopUploaded: `${baseQueryTwo} AND a.other16 = 'Completed' AND a.other28 IS NOT NULL ${whereClause}`,
      };
      const responseObj = {};
      for (const query in queries) {
        const result = await fetchBaseQuery(queries[query]);
        responseObj[query] = {
          count: result[0]?.Column1,
          query: queries[query],
        };
      }
      setTotalSopReport(responseObj);
    } catch (error) {
      console.log("error occured in fetchSopReports", error);
      setTotalReportLoading(false);
    } finally {
      setTotalReportLoading(false);
    }
  };

  const fetchSopUserReports = async () => {
    setAdmUserSopReportLoading(true);
    try {
      const coordinatorList = await fetchBaseQuery(
        "SELECT um.UserCode, um.UserName, um.Active FROM Usermas um WHERE um.UserCode IN (162, 176, 191, 198, 238, 242)"
      );

      if (!Array.isArray(coordinatorList) || coordinatorList.length === 0) {
        console.log("No records to execute");
        return [];
      }
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );
      const stStatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );
      const packageCodeCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      [
        intakeCondition,
        packageCondition,
        locationCondition,
        stStatusCondition,
        packageCodeCondition,
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });
      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : ``;

      const baseQuery = "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode LEFT JOIN Stdassignment sa ON s.StdCode = sa.stdcode LEFT JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND s.packageCode IN (1, 2, 3, 5, 6, 8, 11, 12) AND s.uinliststatus IN (1, 4, 5, 6)"
      const baseQueryTwo = "SELECT COUNT(DISTINCT a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode LEFT JOIN Stdassignment sa ON s.StdCode = sa.stdcode LEFT JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND s.packageCode IN (1, 2, 3, 5, 6, 8, 11, 12) AND s.uinliststatus IN (1, 4, 5, 6)"
      const queries = {
        totalStudents: `${baseQuery} ${whereClause}`,
        approvedApplication: `${baseQueryTwo} ${whereClause}`,
        sopNotRequired: `${baseQueryTwo} AND a.other16 = 'SOP not required' ${whereClause}`,
        assgToFreelancer: `${baseQueryTwo} AND a.other16 = 'Assigned to freelancer' ${whereClause}`,
        assgToYg: `${baseQueryTwo} AND a.other16 = 'Assigned to YG team' ${whereClause}`,
        cvPending: `${baseQueryTwo} AND a.other16 = 'CV Pending' ${whereClause}`,
        sopDraftCreated: `${baseQueryTwo} AND a.other16 = 'SOP draft created' ${whereClause}`,
        firstDraftUploaded: `${baseQueryTwo} AND a.statusFromYGTeam = 1 AND a.other16 = 'First draft uploaded' ${whereClause}`,
        inReview: `${baseQueryTwo} AND a.other16 = 'In Review' ${whereClause}`,
        completed: `${baseQueryTwo} AND a.other16 = 'Completed' ${whereClause}`,
        completed_notUploaded: `${baseQueryTwo} AND a.other16 = 'Completed' AND a.other28 IS NULL ${whereClause}`,
        sopUploaded: `${baseQueryTwo} AND a.other16 = 'Completed' AND a.other28 IS NOT NULL ${whereClause}`,
      };

      const responseObj = [];
      for (const item of coordinatorList) {
        const coordinator = item.USERCODE;
        const coordinatorData = {
          name: item?.USERNAME,
          status: item?.ACTIVE,
          details: {},
        };

        for (const [key, baseQuery] of Object.entries(queries)) {
          // Inject counsellor condition
          const finalQuery = `${baseQuery} AND um.UserCode = '${coordinator}'`;
          try {
            const result = await fetchBaseQuery(finalQuery);
            coordinatorData.details[key] = {
              query: finalQuery,
              count: result[0]?.Column1 || 0,
            };
          } catch (error) {
            console.error(`Error fetching ${key} for ${coordinator}:`, error);
            coordinatorData.details[key] = null;
          }
        }
        responseObj.push(coordinatorData);
      }

      setAdmSopUserReport(responseObj);
    } catch (error) {
      console.log("error occured in fetchSopReports", error);
      setAdmUserSopReportLoading(false);
    } finally {
      setAdmUserSopReportLoading(false);
    }
  };

  const fetchvisaSopReports = async () => {
    setVisaSopReportLoading(true);
    try {
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location
      );

      const stStatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );

      const packageCodeCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );

      [
        intakeCondition,
        packageCondition,
        locationCondition,
        stStatusCondition,
        packageCodeCondition,
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });

      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : ``;

      const queries = {
        totalStudents: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode WHERE s.StdCode IS NOT NULL AND a.applicationId IS NOT NULL AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND s.packageCode IN (2, 3, 4, 5, 10) AND s.uinliststatus IN (1, 4, 5, 6) ${whereClause}`,
        // admAmtRecieved: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode WHERE a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND s.packageCode IN (2, 3, 4, 5, 10) AND s.PaymentStatus IN ('Completed', 'Visa Process Started', 'Visa Amount pending', 'Accommodation Amount pending') ${whereClause}`,
        finalAccepted: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode WHERE a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.status = 'Final Admission Accepted' ${whereClause} `,
        notUpdated: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode WHERE a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.status = 'Final Admission Accepted' AND (a.visaSopstatus IS NULL OR a.visaSopstatus = 0) ${whereClause} `,
        completed: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 107 ${whereClause}`,
        completed_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 107 AND a.VisaSOP IS NOT NULL ${whereClause}`,
        completed_not_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 107 AND a.VisaSOP IS NULL ${whereClause}`,
        completed_wrong_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 108 ${whereClause}`,
        completed_under_process_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 106 ${whereClause}`,
        completed_draft_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 105 ${whereClause}`,
        completed_first_draft_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 110 ${whereClause}`,
        completed_in_review_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 109 ${whereClause}`,
      };
      const responseObj = {};
      for (const query in queries) {
        const result = await fetchBaseQuery(queries[query]);
        responseObj[query] = {
          count: result[0]?.Column1,
          query: queries[query],
        };
      }
      setVisaSopReport(responseObj);
    } catch (error) {
      console.log("error occured in fetchSopReports", error);
      setVisaSopReportLoading(false);
    } finally {
      setVisaSopReportLoading(false);
    }
  };

  const fetchVisaSopUserReports = async () => {
    setUserWiseVisaLoading(true);
    try {
      const coordinatorList = await fetchBaseQuery(
        "SELECT um.UserCode, um.UserName, um.Active FROM Usermas um WHERE um.UserCode IN (162, 176, 191, 198, 238, 242)"
      );

      if (!Array.isArray(coordinatorList) || coordinatorList.length === 0) {
        console.log("No records to execute");
        return [];
      }
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );

      const stStatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );

      const packageCodeCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      [
        intakeCondition,
        packageCondition,
        locationCondition,
        stStatusCondition,
        packageCodeCondition,
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });
      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : ``;

      const queries = {
        totalStudents: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode JOIN Stdassignment sa ON s.StdCode = sa.stdcode JOIN Usermas um ON um.UserCode = sa.UserCode WHERE s.StdCode IS NOT NULL AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND s.packageCode IN (2, 3, 4, 5, 10) AND s.uinliststatus IN (1, 4, 5, 6) ${whereClause}`,
        admAmtRecieved: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode JOIN Stdassignment sa ON s.StdCode = sa.stdcode JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND s.PaymentStatus IN ('Completed', 'Visa Process Started', 'Visa Amount pending', 'Accommodation Amount pending') ${whereClause}`,
        finalAccepted: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode JOIN Stdassignment sa ON s.StdCode = sa.stdcode JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.status = 'Final Admission Accepted' ${whereClause} `,
        notUpdated: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode JOIN Stdassignment sa ON s.StdCode = sa.stdcode JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.status = 'Final Admission Accepted' AND (a.visaSopstatus IS NULL OR a.visaSopstatus = 0) ${whereClause} `,
        completed: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode JOIN Stdassignment sa ON s.StdCode = sa.stdcode JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 107 ${whereClause}`,
        completed_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode JOIN Stdassignment sa ON s.StdCode = sa.stdcode JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 107 AND a.VisaSOP IS NOT NULL ${whereClause}`,
        completed_not_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode JOIN Stdassignment sa ON s.StdCode = sa.stdcode JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 107 AND a.VisaSOP IS NULL ${whereClause}`,
        completed_wrong_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode JOIN Stdassignment sa ON s.StdCode = sa.stdcode JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 108 ${whereClause}`,
        completed_under_process_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode JOIN Stdassignment sa ON s.StdCode = sa.stdcode JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 106 ${whereClause}`,
        completed_draft_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode JOIN Stdassignment sa ON s.StdCode = sa.stdcode JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 105 ${whereClause}`,
        completed_first_draft_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode JOIN Stdassignment sa ON s.StdCode = sa.stdcode JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 110 ${whereClause}`,
        completed_in_review_uploaded: `SELECT COUNT(a.applicationId) FROM StudentMas s LEFT JOIN AdmissionMas a ON a.StdCode = s.StdCode JOIN Stdassignment sa ON s.StdCode = sa.stdcode JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.status = 'Final Admission Accepted' AND a.visaSopstatus = 109 ${whereClause}`,
      };

      const responseObj = [];
      for (const item of coordinatorList) {
        const coordinator = item.USERCODE;
        const coordinatorData = {
          name: item?.USERNAME,
          status: item?.ACTIVE,
          details: {},
        };

        for (const [key, baseQuery] of Object.entries(queries)) {
          // Inject counsellor condition
          const finalQuery = `${baseQuery} AND um.UserCode = '${coordinator}'`;
          try {
            const result = await fetchBaseQuery(finalQuery);
            coordinatorData.details[key] = {
              query: finalQuery,
              count: result[0]?.Column1 || 0,
            };
          } catch (error) {
            console.error(`Error fetching ${key} for ${coordinator}:`, error);
            coordinatorData.details[key] = null;
          }
        }

        responseObj.push(coordinatorData);
      }
      setVisaWiseCvReport(responseObj);
    } catch (error) {
      setUserWiseVisaLoading(false);
      console.log("error occured in fetchSopReports", error);
    } finally {
      setUserWiseVisaLoading(false);
    }
  };

  const checkAuthentication = async () => {
    try {
      if (userCode === 0) {
        setAuthState(true);
        return true;
      }

      const checkAdmin = await fetchBaseQuery(
        `SELECT * FROM usermas WHERE UserCode = ${userCode} AND deptcode IN (15, 17, 16, 14, 12)`
      );

      if (Array.isArray(checkAdmin) && checkAdmin.length > 0) {
        setAuthState(true);
        return true;
      }

      setAuthState(false);
      return false;
    } catch (error) {
      console.error("Error checking authentication:", error);
      setAuthState(false);
      return false;
    }
  };
  useEffect(() => {
      checkAuthentication();
    }, [userCode]);

  useEffect(() => {
    fetchFilterDropdown();
    fetchvisaSopReports();
    fetchSopReports();
    fetchCvReports();
    fetchCvReportsUserWise();
    fetchSopUserReports();
    fetchVisaSopUserReports();
  }, [filterOptions]);

  useEffect(() => {
    checkAuthentication();
  }, []);

  return authState ?
   (
    <div className="transback h-100 overflow-auto p-3 d-flex flex-column">
      {user ? (
        <>
          <button
            className="btn text-secondary rounded-pill px-3 py-1 d-flex d-lg-none align-items-center gap-2"
            onClick={() => setShowSideOverlay(true)}
          >
            <MenuIcon />
          </button>
          <div className="headerUserDetails py-1 mb-3 d-flex justify-content-between">
            <span>CV Dashboard</span>
            <div
              className="d-flex gap-2 align-items-center"
              title={`Name: ${user.name} | Email: ${user.email}`}
            >
              <i className="fa fa-user"></i>
              <span>{user.name}</span>
            </div>
          </div>
        </>
      ) : (
        ""
      )}

      <div className="w-100 h-100 overflow-auto ">
        {spinnerLoading ? (
          <div className="w-100 h-100 d-flex align-items-center justify-content-center">
            <CustomSpinner size={1} />
          </div>
        ) : (
          <>
            <div className="container-fluid min-vh-100">
              <div className="w-100">
                <div className="card border-0 bg-white">
                  <div className="card-body">
                    <div className="row row-cols-2 row-cols-lg-6 g-2 g-lg-3 mb-2">
                      <div className="col-md">
                        <div className="form-group">
                          <Select
                            id="package-select"
                            options={filterDropdown.intakeOptions}
                            isMulti
                            isClearable
                            placeholder="Select Intake"
                            onChange={(selectedOptions) => {
                              setFilterOptions((prev) => ({
                                ...prev,
                                intake: selectedOptions
                                  ? selectedOptions.map((opt) =>
                                      parseInt(opt.value)
                                    )
                                  : [],
                              }));
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md">
                        <div className="form-group">
                          <Select
                            id="package-select"
                            options={filterDropdown.locationOptions}
                            isMulti
                            isClearable
                            placeholder="Select Branch"
                            onChange={(selectedOptions) => {
                              setFilterOptions((prev) => ({
                                ...prev,
                                location: selectedOptions
                                  ? selectedOptions.map((opt) =>
                                      parseInt(opt.value)
                                    )
                                  : [],
                              }));
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <Select
                            id="ststatus-select"
                            options={stStatusOptions}
                            isMulti
                            isClearable
                            placeholder="Select Student Status"
                            onChange={(selectedOptions) => {
                              setFilterOptions((prev) => ({
                                ...prev,
                                ststatus: selectedOptions
                                  ? selectedOptions.map((opt) =>
                                      parseInt(opt.value)
                                    )
                                  : [],
                              }));
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <Select
                            id="package-select"
                            options={packageCodes}
                            isMulti
                            isClearable
                            placeholder="Select Package"
                            onChange={(selectedOptions) => {
                              setFilterOptions((prev) => ({
                                ...prev,
                                package: selectedOptions
                                  ? selectedOptions.map((opt) =>
                                      parseInt(opt.value)
                                    )
                                  : [],
                              }));
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <Select
                            id="payment-select"
                            options={paymentStatusOptions}
                            isMulti
                            isClearable
                            placeholder="Select Payment Status"
                            onChange={(selectedOptions) => {
                              setFilterOptions((prev) => ({
                                ...prev,
                                paymentStatus: selectedOptions
                                  ? selectedOptions.map((opt) =>
                                      opt.value
                                    )
                                  : [],
                              }));
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <Select
                            id="category-select"
                            options={filterDropdown.categoryOptions}
                            isMulti
                            isClearable
                            placeholder="Select Category"
                            onChange={(selectedOptions) => {
                              setFilterOptions((prev) => ({
                                ...prev,
                                category: selectedOptions
                                  ? selectedOptions.map((opt) =>
                                      parseInt(opt.value)
                                    )
                                  : [],
                              }));
                            }}
                          />
                        </div>
                      </div>
                      {/* <div className="col-md-2">
                        <div className="form-group">
                          <Select
                            id="category-select"
                            options={cvStatusOptions}
                            isMulti
                            isClearable
                            placeholder="Select CV Status"
                            onChange={(selectedOptions) => {
                              setFilterOptions((prev) => ({
                                ...prev,
                                cvStatus: selectedOptions
                                  ? selectedOptions.map((opt) =>
                                      opt.value
                                    )
                                  : [],
                              }));
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <Select
                            id="planner-select"
                            options={filterDropdown.plannerOptions}
                            isMulti
                            isClearable
                            placeholder="Select Application Status"
                            onChange={(selectedOptions) => {
                              setFilterOptions((prev) => ({
                                ...prev,
                                plannerStatus: selectedOptions
                                  ? selectedOptions.map((opt) =>
                                      opt.value
                                    )
                                  : [],
                              }));
                            }}
                          />
                        </div>
                      </div> */}
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-100">
                <CvTables
                  totalReportLoading={totalReportLoading}
                  totalSopReport={totalSopReport}
                  visaSopReport={visaSopReport}
                  visaSopReportLoading={visaSopReportLoading}
                  cvReportLoading={cvReportLoading}
                  cvReport={cvReport}
                  userWiseLoading={userWiseLoading}
                  cvUserWiseReport={cvUserWiseReport}
                  admSopUserReportLoading={admSopUserReportLoading}
                  admSopUserReport={admSopUserReport}
                  userWiseVisaLoading={userWiseVisaLoading}
                  visaUserWiseReport={visaUserWiseReport}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  ) : ( <NotAuthorize />);
};

export default CvDashboard;
